import {
  Box,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { UserExerciseDto } from "@growth-machine-llc/stridist-api-client";
import ExerciseCard from "./ExerciseCard";
import makeStyles from "@mui/styles/makeStyles";
import TimeRangeButtons, { TimeRangeUnit } from "./components/TimeRangeButtons";
import { useQuery } from "@tanstack/react-query";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import {
  parseWorkoutResultValueJsonToWorkoutSection,
  extractExerciseResultSets,
} from "../workout/utils";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import {
  ExerciseTypeSet,
  getExerciseUnits,
  JourneyAnchors,
  setUnitsTypeOptions,
  setUnitTimeTypeOptions,
  Units,
  UnitsTime,
} from "../../constants";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
dayjs.extend(minMax);
import { getTimeRange } from "../../utils/date";
import ToggleButtonGroup from "./components/ExerciseTypeSetToogleButtonGroup";
import WorkoutHistoryContainer from "./workout-history/WorkoutHistoryContainer";
import WorkoutActivityOverview from "./WorkoutActivityOverview";
import { VolumeChart } from "./charts/VolumeChart";
import WeightChart from "./charts/ExerciseMetricChart";
import RepsChart from "./charts/RepsChart";
import {
  buildExerciseVolumes,
  buildHighlights,
  findDefaultTimeRange,
  getCurrentAndPrevParsedSets,
} from "./utils";
import { Calendar, Settings } from "lucide-react";

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { IconButton } from "@mui/material";
import { BaseDialog } from "../dialog/BaseDialog";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: theme.spacing(2, 0),
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  chartsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    flex: 1,
    height: "100%",
  },
  historySection: {
    flexShrink: 0,
    width: 360,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  timeRangeText: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
  },
  navigationButtonsWithUnits: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexDirection: "row",
    flexWrap: "wrap",
  },
  dialogPaper: {
    width: theme.spacing(65.5),
  },
  timeRangeContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  timeRangeWithSettingsIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 1.5,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "space-between",
    },
  },
}));

interface IClientExerciseHistoryContainer {
  userExercise: UserExerciseDto;
  clientId: number;
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  onOpenExerciseDrawer?: () => void;
}

export const EXERCISE_HISTORY_QUERY_KEY = "exercise-history";

const DEFAULT_TIME_RANGE_VIEW = TimeRangeUnit.MONTH;
const MIN_ENTRIES_FOR_DEFAULT_VIEW = 4;

const ClientExerciseHistoryContainer = ({
  userExercise,
  clientId,
  onExerciseSelect,
  onOpenExerciseDrawer,
}: IClientExerciseHistoryContainer) => {
  const s = useStyles();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const exerciseAsset = userExercise.exercise;
  const [activeButton, setActiveButton] = React.useState(
    DEFAULT_TIME_RANGE_VIEW,
  );

  // render nothing until the default view to improve user experience
  const [defaultViewSelected, setDefaultViewSelected] = React.useState(false);

  const [navIndex, setNavIndex] = React.useState(0);
  const [exerciseTypeSet, setExerciseTypeSet] =
    React.useState<ExerciseTypeSet | null>(null);
  const { units: currentUserUnits } = useCurrentUser();
  const [units, setUnits] = React.useState<string>(currentUserUnits);

  const [isUnitsDialogOpen, setIsUnitsDialogOpen] = React.useState(false);

  const { data: workoutExerciseResultsData, isLoading } = useQuery({
    queryKey: [
      EXERCISE_HISTORY_QUERY_KEY,
      { clientId, exerciseId: exerciseAsset?.id },
    ],
    queryFn: () =>
      WorkoutResultsService.getUserExerciseHistory(
        clientId,
        exerciseAsset.id,
        null,
      ),
    enabled: !!clientId && !!exerciseAsset,
  });

  const workoutExerciseResults = React.useMemo(() => {
    return (
      workoutExerciseResultsData?.items.map(({ value, ...dto }) => ({
        ...dto,
        activity: {
          ...dto.activity,
          completedAt: dto.activity?.completedAt?.toISOString(),
        },
        section: parseWorkoutResultValueJsonToWorkoutSection(value),
      })) || []
    );
  }, [workoutExerciseResultsData]);

  const validWorkoutResults = React.useMemo(() => {
    return workoutExerciseResults.filter((item) => item.activity?.completedAt);
  }, [workoutExerciseResults]);

  const allResultExerciseSets = React.useMemo(() => {
    return extractExerciseResultSets(
      validWorkoutResults,
      exerciseAsset.id,
      units,
      null,
    );
  }, [validWorkoutResults, exerciseAsset.id, units]);

  const availableExerciseTypes = React.useMemo(() => {
    const types = new Set(
      allResultExerciseSets
        .map((rs) => rs.exerciseTypeSet)
        .filter((t): t is ExerciseTypeSet => Boolean(t)),
    );
    return Array.from(types);
  }, [allResultExerciseSets]);

  const showExerciseTypeSetToggle = availableExerciseTypes.length > 1;

  React.useEffect(() => {
    if (
      !exerciseTypeSet &&
      availableExerciseTypes.length > 0
    ) {
      const exerciseTypeToSet = availableExerciseTypes[0];
      setExerciseTypeSet(exerciseTypeToSet);
      if (exerciseTypeToSet === ExerciseTypeSet.TIME){
        setUnits(UnitsTime.MINUTES);
      } else {
        setUnits(currentUserUnits);
      }
    }
  }, [ availableExerciseTypes, currentUserUnits]);

  const allFilteredExerciseSets = React.useMemo(() => {
    return extractExerciseResultSets(
      validWorkoutResults,
      exerciseAsset.id,
      units,
      showExerciseTypeSetToggle ? exerciseTypeSet : null,
    );
  }, [
    validWorkoutResults,
    exerciseAsset.id,
    units,
    exerciseTypeSet,
    showExerciseTypeSetToggle,
  ]);

  const { currentResults, prevResults } = React.useMemo(() => {
    return getCurrentAndPrevParsedSets(
      allFilteredExerciseSets,
      activeButton,
      navIndex,
    );
  }, [allFilteredExerciseSets, activeButton, navIndex]);

  const exerciseVolumes = React.useMemo(
    () => buildExerciseVolumes(currentResults),
    [currentResults],
  );

  const { start: startDate, end: endDate } = React.useMemo(
    () => getTimeRange(activeButton, navIndex),
    [activeButton, navIndex],
  );

  const minDate = React.useMemo(
    () =>
      allFilteredExerciseSets.length
        ? dayjs.min(allFilteredExerciseSets.map((r) => dayjs(r.completedAt)))
        : null,
    [allFilteredExerciseSets],
  );

  const maxDate = React.useMemo(
    () =>
      allFilteredExerciseSets.length
        ? dayjs.max(allFilteredExerciseSets.map((r) => dayjs(r.completedAt)))
        : null,
    [allFilteredExerciseSets],
  );

  // set the nav index to show the most recent date range on first load
  // if there are not enough entries in that range, switch to all time view
  React.useEffect(() => {
    if (!maxDate) {
      setDefaultViewSelected(true);
      return;
    }

    const { unit, index } = findDefaultTimeRange({
      maxDate,
      records: allFilteredExerciseSets,
      minEntries: MIN_ENTRIES_FOR_DEFAULT_VIEW,
      defaultUnit: DEFAULT_TIME_RANGE_VIEW,
    });

    setActiveButton(unit);
    setNavIndex(index);
    setDefaultViewSelected(true);
  }, [allFilteredExerciseSets, maxDate]);

  const { start: currentStart, end: currentEnd } = React.useMemo(
    () => getTimeRange(activeButton, navIndex),
    [activeButton, navIndex],
  );

  const calendarMaxDate = currentResults.length
    ? dayjs.max(currentResults.map((r) => dayjs(r.completedAt)))
    : currentEnd;

  const disablePrev =
    activeButton === TimeRangeUnit.ALL_TIME ||
    !minDate ||
    currentStart?.isSame(minDate, "day") ||
    currentStart?.isBefore(minDate, "day");
  const today = dayjs();

  const disableNext =
    activeButton === TimeRangeUnit.ALL_TIME ||
    !currentEnd ||
    currentEnd.isSame(today, "day") ||
    currentEnd.isAfter(today, "day");

  const highlights = React.useMemo(() => {
    return buildHighlights(
      currentResults,
      prevResults,
      units as Units,
      exerciseTypeSet,
      activeButton !== TimeRangeUnit.ALL_TIME && !disablePrev,
      activeButton === TimeRangeUnit.MONTH && navIndex === 0,
    );
  }, [
    currentResults,
    prevResults,
    units,
    exerciseTypeSet,
    exerciseAsset.id,
    showExerciseTypeSetToggle,
    activeButton,
    disablePrev,
  ]);

  const renderToggleAndUnits = (mobile: boolean) => (
    <Box
      display="flex"
      flexDirection="row"
      gap={2.5}
      justifyContent={mobile ? "flex-start" : "flex-end"}
      alignItems="center"
      height={60}
    >
      <ToggleButtonGroup
        value={exerciseTypeSet || ExerciseTypeSet.WEIGHT}
        setValue={(value) => {
          setExerciseTypeSet(value);
          if (value === ExerciseTypeSet.TIME) setUnits(UnitsTime.MINUTES);
          else setUnits(currentUserUnits);
        }}
        availableValues={availableExerciseTypes}
      />
      <TextField
        color="primary"
        size="small"
        select
        value={units}
        className={JourneyAnchors.UNITS_TOGGLE}
        onChange={(event) => setUnits(event.target.value as string)}
        sx={{
          textTransform: "lowercase",
          minWidth: 80,
          ".MuiOutlinedInput-root": {
            fontWeight: 500,
            borderRadius: 2,
            "& fieldset": { borderRadius: 2 },
          },
        }}
        slotProps={{
          input: { sx: { minHeight: 40 } },
        }}
      >
        {exerciseTypeSet === ExerciseTypeSet.TIME
          ? setUnitTimeTypeOptions.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{ textTransform: "lowercase", fontWeight: 500 }}
              >
                {item.labelShortened}
              </MenuItem>
            ))
          : setUnitsTypeOptions.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{ textTransform: "lowercase", fontWeight: 500 }}
              >
                {getExerciseUnits(exerciseTypeSet, item.value)}
              </MenuItem>
            ))}
      </TextField>
    </Box>
  );

  const renderCharts = () => (
    <>
      <VolumeChart
        volumes={exerciseVolumes}
        highlight={highlights.volume}
        units={units as Units}
        type={exerciseTypeSet || ExerciseTypeSet.WEIGHT}
      />
      <WeightChart
        volumes={exerciseVolumes}
        highlight={highlights.metric}
        units={units as Units}
        type={exerciseTypeSet || ExerciseTypeSet.WEIGHT}
      />
      <RepsChart volumes={exerciseVolumes} highlight={highlights.reps} />
    </>
  );

  const renderHistoryPanel = () => (
    <>
      <WorkoutActivityOverview
        clientId={clientId}
        exerciseAsset={exerciseAsset}
        onExerciseSelect={onExerciseSelect}
        endDate={
          activeButton === TimeRangeUnit.ALL_TIME ? maxDate : calendarMaxDate
        }
      />
      <WorkoutHistoryContainer
        resultSets={currentResults}
        isLoading={isLoading}
        exerciseTypeSet={exerciseTypeSet || ExerciseTypeSet.WEIGHT}
        units={units as Units}
      />
    </>
  );

  const renderTimeRangeText = () => {
    const min = activeButton === TimeRangeUnit.ALL_TIME ? minDate : startDate;
    const max = activeButton === TimeRangeUnit.ALL_TIME ? maxDate : endDate;
    if (!min || !max) return null;
    return (
      <Box className={s.timeRangeText}>
        <Calendar size={16} />
        <Typography variant="body2" color="textSecondary" paddingLeft={1}>
          {dayjs(min).format("MMM D, YYYY")} -{" "}
          {dayjs(max).format("MMM D, YYYY")}
        </Typography>
      </Box>
    );
  };

  const renderMobile = () => (
    <>
      <ExerciseCard
        exercise={exerciseAsset}
        title={userExercise.title}
        onOpenExerciseDrawer={onOpenExerciseDrawer}
      />
      <Box
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        sx={{ width: "100%" }}
      >
        {/* Temporary solution for the desktop layout, should be update later */}
        <Box flex="1 1 auto" minWidth={0}>
          <TimeRangeButtons
            activeButton={activeButton}
            setActiveButton={setActiveButton}
            navIndex={navIndex}
            setNavIndex={setNavIndex}
            disablePrev={disablePrev}
            disableNext={disableNext}
          />
        </Box>

        <Box className={s.timeRangeContainer}>
          {!isDownMd && renderToggleAndUnits(true)}

          <Box className={s.timeRangeWithSettingsIcon}>
            {renderTimeRangeText()}

            {isDownMd && (
              <IconButton onClick={() => setIsUnitsDialogOpen(true)}>
                <Settings size={16} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {renderCharts()}
      {renderHistoryPanel()}

      <BaseDialog
        open={isUnitsDialogOpen}
        onClose={() => setIsUnitsDialogOpen(false)}
        title="Settings"
        PaperProps={{ className: s.dialogPaper }}
      >
        {renderToggleAndUnits(true)}
      </BaseDialog>
    </>
  );

  const renderDesktop = () => (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexDirection={"column"}
      >
        <Box className={s.header}>
          <ExerciseCard
            exercise={exerciseAsset}
            title={userExercise.title}
            onOpenExerciseDrawer={onOpenExerciseDrawer}
          />
          <Box className={s.navigationButtonsWithUnits}>
            <TimeRangeButtons
              activeButton={activeButton}
              setActiveButton={setActiveButton}
              navIndex={navIndex}
              setNavIndex={setNavIndex}
              disablePrev={disablePrev}
              disableNext={disableNext}
            />
          </Box>
          <Box>{renderToggleAndUnits(false)}</Box>
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} width={"100%"}>
          {renderTimeRangeText()}
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" gap={2.5}>
        <Box className={s.chartsContainer}>{renderCharts()}</Box>
        <Box gap={2.5} className={s.historySection}>
          {renderHistoryPanel()}
        </Box>
      </Box>
    </>
  );

  return (
    <Box className={s.root}>
      {exerciseAsset && !isLoading && defaultViewSelected && (
        <Box display="flex" flexDirection={"column"} gap={2} flex={1}>
          {isDownMd ? renderMobile() : renderDesktop()}
        </Box>
      )}
    </Box>
  );
};

export default ClientExerciseHistoryContainer;
